import { LimitKeys, Timestamp, User } from '@griply/types';
import {
  isPremiumFeatureAllowed,
  LimitCheckFunctionParams,
} from '@griply/utils';
import { isBefore, isSameDay } from 'date-fns';
import { useMemo } from 'react';

export type UsePremiumFeatureAllowedOptions = {
  user: User | null | undefined;
  today: Timestamp;
};

// Stable reference for keys that take no extra params, so the memo below actually holds.
export const NO_PREMIUM_OPTIONS = {};

export type PremiumFeatureAllowed = {
  limit: number;
  enforced: boolean;
  enforcedAt: Timestamp | undefined;
  allowed: boolean;
};

export const resolvePremiumFeatureAllowed = <LimitKey extends LimitKeys>(
  limitKey: LimitKey,
  options: Omit<LimitCheckFunctionParams[LimitKey], 'user' | 'today'>,
  { user, today }: UsePremiumFeatureAllowedOptions,
): PremiumFeatureAllowed => {
  if (!user) {
    return {
      limit: 0,
      enforced: true,
      enforcedAt: today,
      allowed: false,
    };
  }

  const enforceDate = user.limits[limitKey].enforcedAt;

  return {
    limit: user.limits[limitKey].limit,
    enforced: enforceDate
      ? isBefore(enforceDate, today) || isSameDay(enforceDate, today)
      : false,
    enforcedAt: enforceDate,
    allowed: isPremiumFeatureAllowed(limitKey, {
      ...options,
      user,
      today,
    } as LimitCheckFunctionParams[LimitKey]),
  };
};

export const usePremiumFeatureAllowed = <LimitKey extends LimitKeys>(
  limitKey: LimitKey,
  options: Omit<LimitCheckFunctionParams[LimitKey], 'user' | 'today'>,
  { user, today }: UsePremiumFeatureAllowedOptions,
): PremiumFeatureAllowed =>
  useMemo(
    () => resolvePremiumFeatureAllowed(limitKey, options, { user, today }),
    [limitKey, options, today, user],
  );
